import { useEffect } from "react";
import styles from "./styles.module.scss";
import { PlanItem } from "./components/planItem";
import { getAgreementText, getPlanPricing } from "./utils/getPlanDetails";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import { PaymentOptions } from "./components/paymentOptions";
import { StripeElementsProvider } from "./stripeElementsContext";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@common/spinner";
import { Metrics } from "./components/metrics";
import { setActivePlan } from "src/redux/slices/plansSlice";
import { OfferEnds } from "../offerEnds";
import { getFunnelType, getPaywallType } from "src/appConfig";
import { useDiscount } from "@hooks/useDiscount";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_API_KEY
);

export const Plan = ({
  title,
  isMetrics = false,
  id,
  onCloseCheckout,
  additionalDiscount = false,
  showTimer = false,
  variant,
}) => {
  const dispatch = useDispatch();
  const { plans, isLoading, activePlan } = useSelector((state) => state.plans);

  const paywallType = getPaywallType();

  const getRetentionPlans = () => {
    return plans.map((plan) => ({
      ...plan.retention,
      title: plan.title,
      uiDisplayOptions: plan.uiDisplayOptions,
    }));
  };

  const displayedPlans = additionalDiscount ? getRetentionPlans() : plans;
  const isWalky = getPaywallType().toLowerCase().includes("walky");
  const isQuiz =
    getPaywallType() === "QUIZ_PAYWALL" ||
    getPaywallType() === "QUIZ_PAYWALL_V2";
  const showHint = isQuiz || isWalky;
  const showOfferEnds = (id === 2 && isQuiz) || isWalky;

  const sortPlans = (plans) => {
    return [...plans].sort((a, b) => {
      const aHasTrial = a.trialPeriodDuration !== 0;
      const bHasTrial = b.trialPeriodDuration !== 0;

      if (aHasTrial && !bHasTrial) return -1;
      if (!aHasTrial && bHasTrial) return 1;

      return 0;
    });
  };

  const sortedPlans = sortPlans(displayedPlans);
  const { isDiscounted } = useDiscount();
  const agreementText = getAgreementText(activePlan, isDiscounted);

  const isNotLegacyTrial =
    !activePlan?.hasTrial && activePlan?.uiDisplayOptions?.legacyPlan;

  const options = {
    mode: "subscription",
    currency: activePlan?.currency?.toLowerCase() || "usd",
    amount: isNotLegacyTrial
      ? activePlan?.priceAmount
      : activePlan?.trialPriceAmount || 100,
    appearance: {
      variables: {
        borderRadius: "9999px",
        width: "100%",
      },
    },
  };

  console.log(activePlan);

  const handleClickPlan = (plan) => {
    dispatch(setActivePlan(plan));
    localStorage.setItem(
      "isTrial",
      String(Boolean(plan.hasTrial && !plan.uiDisplayOptions.ignoreTrial))
    );
  };

  const setActivePlanWithTrial = () => {
    const plansToUse = additionalDiscount ? getRetentionPlans() : plans;
    const active = plansToUse.find((p) => p.uiDisplayOptions.focused);
    if (!active) return;

    dispatch(setActivePlan(active));
    localStorage.setItem(
      "isTrial",
      String(Boolean(active.hasTrial && !active.uiDisplayOptions.ignoreTrial))
    );
  };

  const handleCloseCheckout = () => {
    setActivePlanWithTrial();
    localStorage.setItem("additionalDiscount", "true");
    onCloseCheckout();
  };

  useEffect(() => {
    if (plans.length > 0) {
      setActivePlanWithTrial();
    }
  }, [plans, additionalDiscount]);

  return !isLoading ? (
    <StripeElementsProvider>
      <Elements stripe={stripePromise} options={options}>
        <div className={styles.content}>
          <div className={styles.choosePlan}>
            <h2>{title}</h2>
            {isMetrics && <Metrics variant={variant} />}
            {showOfferEnds && <OfferEnds variant={variant} />}
            <div className={styles.plans}>
              {sortedPlans.map((plan) => {
                const planProps = getPlanPricing(plan);
                return (
                  <PlanItem
                    {...planProps}
                    hasTrial={
                      plan.hasTrial && !plan.uiDisplayOptions.ignoreTrial
                    }
                    isLegacyPlan={plan.uiDisplayOptions.legacyPlan}
                    isDiscounted={isDiscounted || showTimer}
                    key={plan.id}
                    onClick={() => handleClickPlan(plan)}
                    checked={plan.id === activePlan?.id}
                    variant={variant}
                  />
                );
              })}
            </div>
            {showHint && (
              <div className={styles.hint}>
                👆 Users active for 12+ weeks get better results
              </div>
            )}
            <div className={styles.paymentContainer}></div>
            <PaymentOptions
              id={id}
              activePlan={activePlan}
              onCloseCheckout={handleCloseCheckout}
              showTimer={showTimer}
              variant={variant}
            />
            <div className={styles.agreementContainer}>
              <p className={styles.agreement}>{agreementText}</p>
            </div>
          </div>
        </div>
      </Elements>
    </StripeElementsProvider>
  ) : (
    <div className={styles.spinner}>
      <Spinner />
    </div>
  );
};
